import * as Yup from 'yup';
import { getRequiredText } from '../../../../../global/helpers/transformer';
import { YesNoIndicators } from '../../../../../global/models/api-model';
import { LabelSettingProps } from '../../../../../global/models/epi-model';
import { Person } from '../../../../../global/models/person-model';
import { PersonInfoValidationSchema } from '../../../../../global/validation-schemas/account-information-form-validation';
import { NewHireMailingAddressValidationSchema } from '../../../../../global/validation-schemas/address-form-validation';
import { getInitialEmails, getInitialMailingAddress, getInitialPersonInfo, getInitialPhones } from '../../../../account-info/helpers/form-init-helpers';
import { PersonalInfoFields } from '../../../../account-info/models/form-models';
import { NewHireProps, noSelectPersonId } from '../../../models/arc-epi-props';
import { NewHireStep } from './new-hire-model';

export enum CompensationRecordType {
	ScheduledPayment = 'ScheduledPayment',
	OneTimePayment = 'OneTimePayment',
}

export enum EmploymentType {
	Lay = 'Lay',
	Clergy = 'Clergy',
}

export interface NewHirePersonalInfoValues extends PersonalInfoFields {
	// personal info
	designation_salutation: string;

	// address
	mailing_address_1: string;
	mailing_address_2: string;
	mailing_address_3: string;
	mailing_country: string;
	mailing_city: string;
	mailing_state: string;
	mailing_postal_code: string;

	// phones
	business_phone_country_code: string;
	business_phone_area_code: string;
	business_phone_number: string;
	business_phone_formated: string;
	business_phone_extension: string;

	home_phone_country_code: string;
	home_phone_area_code: string;
	home_phone_number: string;
	home_phone_formated: string;
	home_phone_extension: string;

	mobile_phone_country_code: string;
	mobile_phone_area_code: string;
	mobile_phone_number: string;
	mobile_phone_formated: string;
	mobile_phone_extension: string;

	personal_email_address: string;
	office_email_address: string;

	matched_person: string;

	nonStip?: boolean;
}

export interface NonStipNewHireFormValues extends NewHirePersonalInfoValues {

	// position info
	start_date: string;
	position_title: string;
	position_start_date: string;
	position_end_date: string;
	published_title: string;
	employment_type: string;
	end_date: string;
}

export function getInitialNewHireNonStipValues() {
	return {
		start_date: null,
		position_title: '',
		position_start_date: null,
		position_end_date: null,
		published_title: '',
	};
}

export function getInitialNewHireStipValues() {
	return {
		hire_date: null,
		position_title: '',
		hours: 0,
		record_type: CompensationRecordType.ScheduledPayment,
		compensation_effective_date: null,
		amount: 0,
		base_salary: 0,
		employer_contributions: 0,
		cash_housing_allowance: 0,
		employer_provided_housing: '',
		position_efective_date: null,
		published_title: null,
		scheduled_hours_effective_date: null,
	};
}

function GetClergyNewHireFormValues() {
	return {
		is_TEC: null,
		is_another_denomination: null,
		date_licensing: null,
		diocese_licensed_in: null,
		licensed_by: null,
	};
}

export function getInitialNewHireValues(person: Person, nonStip: boolean): NewHireFormValues {
	const personalInfo = {
		...getInitialPersonInfo(person),
		...getInitialMailingAddress(person),
		...getInitialPhones(person),
		...getInitialEmails(person),
		...GetClergyNewHireFormValues(),
	};

	if (nonStip) {
		return {
			...personalInfo,
			...getInitialNewHireNonStipValues(),
			matched_person: null,
			employment_type: null,
			end_date: null,
		};
	}

	return {
		...personalInfo,
		...getInitialNewHireStipValues(),
		employment_type: null,
		matched_person: null,
		position_efective_date: null,
		published_title: null,
		scheduled_hours_effective_date: null,
	};
}

export function newHireFormValidation(labelSetting: LabelSettingProps, newHireProps: NewHireProps, step: NewHireStep, nonStip: boolean, hasSearchResults: boolean) {
	return Yup.lazy(({
		record_type,
		date_of_birth,
		matched_person,
		business_phone_formated,
		business_phone_country_code,
		mobile_phone_formated,
		mobile_phone_country_code,
		home_phone_formated,
		home_phone_country_code,
		is_TEC,
		is_another_denomination }) => {
		let schema = Yup.object().shape({});

		if (step === NewHireStep.PersonalInfo) {
			if (hasSearchResults && !matched_person) {
				schema = schema.shape({
					matched_person: Yup.string().nullable().required(labelSetting.selectMatchPersonRequired),
				});
			} else if (!hasSearchResults || (hasSearchResults && matched_person === noSelectPersonId)) {
				schema = schema.shape({
					...PersonInfoValidationSchema(labelSetting, date_of_birth,
						{
							business_phone_formated,
							business_phone_country_code,
							mobile_phone_formated,
							mobile_phone_country_code,
							home_phone_formated,
							home_phone_country_code,
						}),
					...NewHireMailingAddressValidationSchema(labelSetting),
					personal_email_address: Yup.string().email(labelSetting.emailFormatError).ensure().when('office_email_address', {
						is: '',
						then: Yup.string().required(labelSetting.atLeastOneEmail),
					}),
					office_email_address: Yup.string().email(labelSetting.emailFormatError).ensure().when('personal_email_address', {
						is: '',
						then: Yup.string().required(labelSetting.atLeastOneEmail),
					}),
				},
					[['office_email_address', 'personal_email_address']]);
			}
		} else if (step === NewHireStep.EmploymentType) {
			schema = schema.shape({
				employment_type: Yup.string().nullable().required(getRequiredText(labelSetting.requiredFormat, newHireProps?.nonStipEmploymentTypeLabel)),
			});
		} else if (step === NewHireStep.ClergyType) {
			if (is_TEC === YesNoIndicators.No) {
				if (is_another_denomination === YesNoIndicators.Yes) {
					schema = schema.shape({
						is_TEC: Yup.string().nullable().required(getRequiredText(labelSetting.requiredFormat, newHireProps?.clergyTypeLabel)),
						is_another_denomination: Yup.string().nullable().required(getRequiredText(labelSetting.requiredFormat, newHireProps?.anotherDenominationLabel)),
						date_licensing: Yup.date().nullable().required(getRequiredText(labelSetting.requiredFormat, newHireProps?.dateLicensingLabel)),
						diocese_licensed_in: Yup.string().nullable().required(getRequiredText(labelSetting.requiredFormat, newHireProps?.dioceseLicensedInLabel)),
						licensed_by: Yup.string().nullable().required(getRequiredText(labelSetting.requiredFormat, newHireProps?.licensedByLabel)),
					});
				} else {
					schema = schema.shape({
						is_TEC: Yup.string().nullable().required(getRequiredText(labelSetting.requiredFormat, newHireProps?.clergyTypeLabel)),
						is_another_denomination: Yup.string().nullable().required(getRequiredText(labelSetting.requiredFormat, newHireProps?.anotherDenominationLabel)),
					});
				}
			} else {
				schema = schema.shape({
					is_TEC: Yup.string().nullable().required(getRequiredText(labelSetting.requiredFormat, newHireProps?.clergyTypeLabel)),
				});
			}
		} else if (step === NewHireStep.HireDetails ||
			step === NewHireStep.EmploymentCompensationClergy ||
			step === NewHireStep.EmploymentCompensationLay) {
			if (nonStip) {
				schema = schema.shape({
					start_date: Yup.date().nullable().required(getRequiredText(labelSetting.requiredFormat, newHireProps?.nonStipStartDateLabel)),
					end_date: Yup.date().nullable().when(
						'position_end_date',
						{
							is: value => !!value,
							then: Yup.date().nullable().required(getRequiredText(labelSetting.requiredFormat, newHireProps?.nonStipTerminationDateLabel)),
						},
					),
					position_title: Yup.string().ensure().when('employment_type', {
						is: newHireProps.clergyLabelText,
						then: Yup.string().required(getRequiredText(labelSetting.requiredFormat, newHireProps?.positionTitleLabel)),
					}),
					position_end_date:
						Yup.
							date().
							nullable().
							notRequired().
							min(Yup.ref('position_start_date'), labelSetting?.endDateBeforeStartDateError || 'End date cannot be before the start date').
							min(Yup.ref('start_date'), labelSetting?.endDateBeforeStartDateError || 'End date cannot be before the start date').
							when(
								'end_date', {
								is: value => !!value,
									then: Yup.date().nullable().required(getRequiredText(labelSetting.requiredFormat, newHireProps?.positionEndDateLabel)).max(Yup.ref('end_date'), labelSetting?.endDateAfterEndDateError || 'End date cannot be after the end date'),
							}),
				}, [['end_date', 'position_end_date']]);
			} else {
				schema = schema.shape({
					hire_date: Yup.date().nullable().required(getRequiredText(labelSetting.requiredFormat, newHireProps?.employmentHireDateLabel)),
					position_title: Yup.string().required(getRequiredText(labelSetting.requiredFormat, newHireProps?.positionTitleLabel)),
					hours: Yup.number().positive(labelSetting.numberPositive),
					record_type: Yup.string().required(getRequiredText(labelSetting.requiredFormat, newHireProps?.recordTypeLabel)),
					compensation_effective_date: Yup.date().nullable().required(getRequiredText(labelSetting.requiredFormat, newHireProps?.compEffectiveDateLabel)),
					position_efective_date: Yup.date().nullable().required(getRequiredText(labelSetting.requiredFormat, newHireProps?.positionEffectiveDateLabel)),
					scheduled_hours_effective_date: Yup.date().nullable().required(getRequiredText(labelSetting.requiredFormat, newHireProps?.scheduledHoursEffectiveDateLabel)),
					published_title: Yup.string().nullable(),
				});

				if (record_type === CompensationRecordType.OneTimePayment) {
					schema = schema.shape({
						amount: Yup.number().round('floor').moreThan(-1, labelSetting.notNegative),
					});
				} else {
					schema = schema.shape({
						base_salary: Yup.number().round('floor').moreThan(-1, labelSetting.notNegative),
						employer_contributions: Yup.number().round('floor').moreThan(-1, labelSetting.notNegative),
						cash_housing_allowance: Yup.number().round('floor').moreThan(-1, labelSetting.notNegative),
						employer_provided_housing: Yup.string().required(getRequiredText(labelSetting.requiredFormat, newHireProps?.employerProvidedHousingLabel)),
					});
				}
			}
		}

		return schema;
	});
}

export interface StipNewHireFormValues extends NewHirePersonalInfoValues {
	hire_date: string;
	position_title: string;
	hours: number;
	record_type: CompensationRecordType,
	compensation_effective_date: string;
	amount: number;
	base_salary: number;
	employer_contributions: number;
	cash_housing_allowance: number;
	employer_provided_housing: string;
	position_efective_date: string;
	published_title: string;
	scheduled_hours_effective_date: string;
}

export interface ClergyNewHireFormValues extends StipNewHireFormValues, NonStipNewHireFormValues {
	is_TEC: string;
	is_another_denomination: string;
	date_licensing: string;
	diocese_licensed_in: string;
	licensed_by: string;
}

export type NewHireFormValues = StipNewHireFormValues | NonStipNewHireFormValues | ClergyNewHireFormValues;


export const dioceseLicensedInOptionsSampleValues = [
	{
		text: 'Diocese of Alabama',
		value: 'Diocese of Alabama',
	},
	{
		text: 'Diocese of Florida',
		value: 'Diocese of Florida',
	},
];

export const licensedByOptionsSampleValues = [
	{
		text: 'Bishop Steven Robers',
		value: 'Bishop Steven Robers',
	},
	{
		text: 'Bishop Diego Maradona',
		value: 'Bishop Diego Maradona',
	},
];